import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
    Search, Trash2, Save, Send, Ban, Truck, Gift, AlertTriangle,
    ClipboardList, FileText, PackagePlus, CalendarClock, ChevronLeft, Loader2,
} from 'lucide-react';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination';
import CommonLoader from '../../components/CommonLoader';
// Imported under a non-`use` name on purpose — the default export of
// hooks/useAxiosAuth.js is a plain FACTORY, and binding it as `useAxiosAuth`
// makes react-hooks/rules-of-hooks flag every call site. Same as MedicineMaster.
import createAuthedAxios from '../../hooks/useAxiosAuth';
import usePermissions from '../../hooks/usePermissions';

const MySwal = withReactContent(Swal);

// THE RECEIVING BAY. Somebody is standing at a delivery with a distributor's
// printed invoice in one hand and a carton in the other, and this screen is the
// place that invoice becomes stock. Everything about its layout follows from
// that one fact.
//
// WHY IT IS A GRID AND NOT A FORM
// A distributor's invoice is a table of twenty lines, and it is keyed straight
// down the columns: batch, expiry, quantity, rate. A modal per line would double
// the keystrokes and — more importantly — hide the running total, which is the
// only thing that tells the clerk they have keyed it correctly. So the lines are
// a grid, the totals sit beside it, and the clerk types the supplier's printed
// grand total into the reconcile box: if the two agree the invoice is keyed
// right, and if they do not the difference is shown before anything is saved.
// That check is the whole reason a receipt can be trusted afterwards.
//
// WHY FREE QUANTITY HAS ITS OWN COLUMN
// A 10+1 scheme means ELEVEN units go on the shelf and TEN are paid for. It is
// tempting — and every pharmacy system that gets this wrong does it — to key
// eleven units with a discount that makes the money come out right. That books
// stock of 10 where the shelf holds 11, and the gap is found months later at a
// physical count, where it is indistinguishable from theft. Free quantity is
// therefore a first-class column that feeds the batch and NOTHING on the money
// side, and the grid prints "goes to shelf" separately from "paid for" so the
// difference is visible while it is being keyed.
//
// WHY THE TAX IS ADDED AND NOT BACKED OUT
// This is the inverse of the counter. On a SALE the MRP is tax-inclusive by law
// and the tax is divided back out. On a PURCHASE the distributor quotes a rate
// and ADDS the tax on top. Reusing the sales rule here would understate every
// line by the tax rate and would look completely plausible on screen. The
// arithmetic below mirrors Backend/services/pharmacy/purchaseService.js
// computePurchaseLine exactly, in paise, so what the clerk reconciles against
// the paper is the same number the server will store.
//
// NOTHING HERE CREATES STOCK. Saving produces a DRAFT. Posting is the separate,
// deliberate act that creates the batches and writes the stock ledger, and it is
// irreversible in the sense that matters: a posted receipt cannot be edited,
// only cancelled and re-keyed. The two buttons are therefore not the same
// colour and the posting one says what it is about to do.
//
// NO REDUX SLICE, for the same reason MedicineMaster has none: a slice must be
// registered in src/store.js and this screen ships before that wiring. The calls
// are plain axios through the same authed factory the slices use, so moving them
// later is a copy rather than a rewrite.

// ---------------------------------------------------------------------------
// Money, in paise, exactly as the server does it
// ---------------------------------------------------------------------------

// Math.round breaks ties toward +Infinity (-2.5 becomes -2 while 2.5 becomes 3).
// The server rounds half AWAY FROM ZERO so that a reversal is exactly the
// negative of the receipt; if this screen rounded differently, the totals the
// clerk reconciled against the paper would differ from the stored ones by a
// paisa and nobody would be able to say which was right.
const roundHalfAwayFromZero = (n) => (n < 0 ? -Math.round(-n) : Math.round(n));

/** Scale a DECIMAL(_,2) to integer hundredths: money to paise, a rate to basis points. */
const scale2 = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n)) return 0;
    return roundHalfAwayFromZero(n * 100);
};
const unscale2 = (scaled) => scaled / 100;

// (a * b) / divisor over integers. A float multiplication of a rate by a
// quantity is the other half of the same precision defect the paise arithmetic
// exists to remove: 0.1 has no binary representation, so a twenty-line invoice
// assembled in rupees drifts and stops matching the supplier's printed total.
const mulDiv = (a, b, divisor) => roundHalfAwayFromZero((a * b) / divisor);

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};
const money = (v) => num(v).toFixed(2);

/**
 * Cost one line the way the PURCHASE side does it — tax ADDED on top.
 *
 *   gross     = rate * qtyPaid          (qtyFree is absent, deliberately)
 *   discount  = explicit rupees, else gross * percent/100
 *   taxable   = gross - discount
 *   tax       = taxable * gstRate/100   (ADDED, never extracted)
 *   lineTotal = taxable + tax
 *
 * Mirrors purchaseService.computePurchaseLine. It is duplicated rather than
 * fetched because the clerk needs the line total to appear as they type — a
 * round trip per keystroke would make the grid unusable — and the server
 * recomputes every figure on save anyway, so this copy can only ever be wrong on
 * screen, never in the database. Any disagreement shows up immediately as a
 * reconcile mismatch, which is exactly where it should be caught.
 */
function costLine(line, isInterState) {
    const rateP = scale2(line.purchaseRate);
    const qtyU = scale2(line.qtyReceived);
    const freeU = scale2(line.qtyFree);

    const grossP = Math.max(0, mulDiv(rateP, qtyU, 100));

    // AN EXPLICIT AMOUNT BEATS THE PERCENTAGE, and both are kept: the percent is
    // what was negotiated and what the supplier's bill prints, the amount is what
    // was actually taken off after rounding to paise. Re-deriving one from the
    // other reintroduces the rounding difference the invoice was balanced against.
    const hasAmount =
        line.discountAmount !== undefined && line.discountAmount !== null && line.discountAmount !== '';
    let discountP = hasAmount
        ? scale2(line.discountAmount)
        : mulDiv(grossP, scale2(line.discountPercent), 10000);
    if (discountP < 0) discountP = 0;
    // Clamped for DISPLAY only, and flagged as an error separately below. The
    // server refuses a discount larger than the line — silently clamping it here
    // would let the clerk save a fat-fingered figure and see a total that looks
    // right.
    const overDiscounted = discountP > grossP;
    if (overDiscounted) discountP = grossP;

    const taxableP = grossP - discountP;
    const rateBp = Math.min(Math.max(scale2(line.gstRate), 0), 10000);

    // Rounded ONCE, and the intra-state split is taken out of that single figure
    // rather than computed twice — so taxable + cgst + sgst is identically the
    // line total and the document's own columns add up to its own total.
    const taxP = mulDiv(taxableP, rateBp, 10000);
    let cgstP = 0;
    let sgstP = 0;
    let igstP = 0;
    if (taxP !== 0) {
        if (isInterState) {
            igstP = taxP;
        } else {
            // An odd number of paise has no half and has to land somewhere. The
            // remainder goes to CGST deterministically and SGST is the difference,
            // so the pair sums to exactly the tax charged — the two halves are
            // claimed separately in the return, where a paisa gap is an input tax
            // credit mismatch.
            cgstP = roundHalfAwayFromZero(taxP / 2);
            sgstP = taxP - cgstP;
        }
    }

    return {
        grossValue: unscale2(grossP),
        discountAmount: unscale2(discountP),
        taxableAmount: unscale2(taxableP),
        cgst: unscale2(cgstP),
        sgst: unscale2(sgstP),
        igst: unscale2(igstP),
        tax: unscale2(taxP),
        lineTotal: unscale2(taxableP + taxP),
        // The three quantities, kept apart on purpose: qtyPaid is what the money
        // was computed on, qtyToStock is what goes on the shelf, and they differ
        // by exactly the scheme quantity.
        qtyPaid: unscale2(qtyU),
        qtyFree: unscale2(freeU),
        qtyToStock: unscale2(qtyU + freeU),
        overDiscounted,
    };
}

/**
 * The header figures, from the costed lines.
 *
 * ROUNDED TO WHOLE RUPEES with the difference disclosed as roundOff, because the
 * supplier's printed total is in whole rupees and goods_receipts has a roundOff
 * column precisely so the receipt can be made to agree with their bill exactly.
 * Derived from the summed paise rather than from already-rounded rupees, so the
 * round-off never exceeds fifty paise.
 */
function costTotals(costed) {
    let subP = 0;
    let discountP = 0;
    let taxableP = 0;
    let cgstP = 0;
    let sgstP = 0;
    let igstP = 0;
    let netP = 0;

    costed.forEach((c) => {
        subP += scale2(c.grossValue);
        discountP += scale2(c.discountAmount);
        taxableP += scale2(c.taxableAmount);
        cgstP += scale2(c.cgst);
        sgstP += scale2(c.sgst);
        igstP += scale2(c.igst);
        netP += scale2(c.lineTotal);
    });

    const grandP = roundHalfAwayFromZero(netP / 100) * 100;
    return {
        subTotal: unscale2(subP),
        discountTotal: unscale2(discountP),
        taxableTotal: unscale2(taxableP),
        cgstTotal: unscale2(cgstP),
        sgstTotal: unscale2(sgstP),
        igstTotal: unscale2(igstP),
        taxTotal: unscale2(cgstP + sgstP + igstP),
        roundOff: unscale2(grandP - netP),
        grandTotal: unscale2(grandP),
    };
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

// The receiving bay's day is India's. A browser in another timezone must not
// decide an Indian pharmacy's expiry cut-off differently from the server, which
// pins the same boundary in SQL (NOW() AT TIME ZONE 'Asia/Kolkata'). Getting this
// wrong is not a rounding error: it makes a pack that expired yesterday look
// acceptable for the first five and a half hours of every working day, and this
// screen is the last point at which that carton can still go back on the van.
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const todayIst = () => new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);

const apiError = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const apiCode = (err) => err?.response?.data?.code || null;

const STATUS_TONE = {
    draft: 'bg-slate-100 text-slate-600 border-slate-300',
    posted: 'bg-emerald-50 text-emerald-700 border-emerald-300',
    cancelled: 'bg-red-50 text-red-600 border-red-300',
};

const blankLine = () => ({
    key: `l${Date.now()}${Math.random().toString(36).slice(2, 7)}`,
    poItemId: null,
    medicineId: '',
    medicineName: '',
    // A PRODUCT may be received with no expiry date (owner, 10 Oct 2026); the
    // server says which lines are products (medicineType) and so does the
    // picker (type). Everything else must carry one.
    isProduct: false,
    packSize: '',
    unit: '',
    batchNo: '',
    expiryDate: '',
    qtyReceived: '',
    qtyFree: '',
    purchaseRate: '',
    mrp: '',
    discountPercent: '',
    discountAmount: '',
    gstRate: '',
});

export default function GoodsReceipt() {
    const { t } = useTranslation();
    const { can, canAny } = usePermissions();
    // One memoised instance: it reads the access token inside its request
    // interceptor, so a single copy stays correct across a token refresh.
    const axiosInstance = useMemo(() => createAuthedAxios(), []);

    const canPurchase = can('pharmacy.purchase.manage');
    // The register is readable by whoever looks after the shelf as well as by the
    // buyer — they are frequently the same person in a small pharmacy — and the
    // routes gate reads on either permission.
    const canRead = canAny(['pharmacy.purchase.manage', 'pharmacy.stock.manage']);

    // 'register' lists what has been received; 'entry' is the keying grid.
    const [view, setView] = useState('register');

    const [stores, setStores] = useState([]);
    const [suppliers, setSuppliers] = useState([]);

    // ---- register ----
    const [receipts, setReceipts] = useState([]);
    const [total, setTotal] = useState(0);
    const [listLoading, setListLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [statusFilter, setStatusFilter] = useState('');
    const [storeFilter, setStoreFilter] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [refreshKey, setRefreshKey] = useState(0);

    // ---- entry ----
    const [grnId, setGrnId] = useState(null);          // set once a draft exists
    const [grnNo, setGrnNo] = useState('');
    const [grnStatus, setGrnStatus] = useState('draft');
    const [storeId, setStoreId] = useState('');
    const [supplierId, setSupplierId] = useState('');
    const [poId, setPoId] = useState('');
    const [poNo, setPoNo] = useState('');
    const [supplierInvoiceNo, setSupplierInvoiceNo] = useState('');
    const [supplierInvoiceDate, setSupplierInvoiceDate] = useState('');
    const [grnDate, setGrnDate] = useState(todayIst());
    const [isInterState, setIsInterState] = useState(false);
    const [notes, setNotes] = useState('');
    const [lines, setLines] = useState([]);
    // What the supplier's own bill says, typed by the clerk. The single most
    // useful field on the screen — see the header.
    const [printedTotal, setPrintedTotal] = useState('');
    const [saving, setSaving] = useState(false);
    const [entryLoading, setEntryLoading] = useState(false);

    // ---- medicine picker ----
    const [pickQuery, setPickQuery] = useState('');
    const [pickResults, setPickResults] = useState([]);
    const [pickOpen, setPickOpen] = useState(false);
    const [picking, setPicking] = useState(false);
    const pickTimeoutRef = useRef(null);
    const pickBoxRef = useRef(null);

    const searchTimeoutRef = useRef(null);
    const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

    const today = useMemo(() => todayIst(), []);

    /* ------------------------------------------------------------------ *
     * Reference data
     * ------------------------------------------------------------------ */

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const [storeRes, supplierRes] = await Promise.all([
                    axiosInstance.get('/pharmacy/stores', { params: { status: 'active' } }),
                    axiosInstance.get('/pharmacy/suppliers', { params: { status: 'active', limit: 200 } }),
                ]);
                if (cancelled) return;
                const storeRows = storeRes?.data?.data || [];
                setStores(storeRows);
                setSuppliers(supplierRes?.data?.data || []);
                // A hospital running one counter should never have to choose it.
                const preferred = storeRows.find((s) => s.isDefault)?.storeId || storeRows[0]?.storeId || '';
                setStoreId((current) => current || preferred);
            } catch (err) {
                if (!cancelled) {
                    toast.warn(apiError(err, t('grnpage.refs_error', 'Could not load counters and suppliers')));
                }
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 500);
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchQuery]);

    /* ------------------------------------------------------------------ *
     * The register
     * ------------------------------------------------------------------ */

    useEffect(() => {
        if (view !== 'register' || !canRead) return undefined;
        let cancelled = false;
        setListLoading(true);
        (async () => {
            try {
                const res = await axiosInstance.get('/pharmacy/grn', {
                    params: {
                        page: currentPage,
                        limit: itemsPerPage,
                        status: statusFilter || undefined,
                        storeId: storeFilter || undefined,
                        q: debouncedSearch || undefined,
                    },
                });
                if (cancelled) return;
                setReceipts(res?.data?.data || []);
                setTotal(res?.data?.total || 0);
            } catch (err) {
                if (!cancelled) toast.warn(apiError(err, t('grnpage.list_error', 'Could not load the receiving register')));
            } finally {
                if (!cancelled) setListLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view, currentPage, itemsPerPage, statusFilter, storeFilter, debouncedSearch, refreshKey, canRead]);

    /* ------------------------------------------------------------------ *
     * The medicine picker
     *
     * ONE SEARCH BOX ABOVE THE GRID, not a lookup inside every row. The clerk
     * finds the product once and then keys straight down the columns of the
     * carton — batch, expiry, quantity — which is the order the information is
     * physically in front of them. A picker per row would put a dropdown between
     * every keystroke.
     * ------------------------------------------------------------------ */

    useEffect(() => {
        if (pickTimeoutRef.current) clearTimeout(pickTimeoutRef.current);
        const q = pickQuery.trim();
        if (q.length < 2 || !storeId) {
            setPickResults([]);
            return undefined;
        }
        pickTimeoutRef.current = setTimeout(async () => {
            setPicking(true);
            try {
                const res = await axiosInstance.get('/pharmacy/medicines/search', {
                    params: { q, storeId, limit: 15 },
                });
                setPickResults(res?.data?.data || []);
                setPickOpen(true);
            } catch (err) {
                toast.warn(apiError(err, t('grnpage.search_error', 'Medicine search failed')));
            } finally {
                setPicking(false);
            }
        }, 350);
        return () => { if (pickTimeoutRef.current) clearTimeout(pickTimeoutRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pickQuery, storeId]);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (pickBoxRef.current && !pickBoxRef.current.contains(e.target)) setPickOpen(false);
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, []);

    const addMedicineLine = (medicine) => {
        setLines((rows) => [
            ...rows,
            {
                ...blankLine(),
                medicineId: medicine.medicineId,
                medicineName: medicine.name,
                isProduct: medicine.type === 'product',
                packSize: medicine.packSize || '',
                unit: medicine.unit || '',
                // The slab is SUGGESTED from the drug master and stays editable:
                // the supplier's invoice is the authority for what was actually
                // charged, and an input tax credit is claimed on what they filed,
                // not on what our catalogue thinks.
                gstRate: medicine.gstRate === null || medicine.gstRate === undefined ? '' : String(medicine.gstRate),
            },
        ]);
        setPickQuery('');
        setPickResults([]);
        setPickOpen(false);
    };

    /* ------------------------------------------------------------------ *
     * Line editing
     * ------------------------------------------------------------------ */

    const patchLine = (key, patch) => {
        setLines((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    };
    const removeLine = (key) => setLines((rows) => rows.filter((row) => row.key !== key));

    // Costed once per render and reused by the grid, the totals and the validator,
    // so the number in the row, the number in the footer and the number that is
    // checked before saving cannot disagree.
    const costed = useMemo(
        () => lines.map((line) => ({ line, cost: costLine(line, isInterState) })),
        [lines, isInterState]
    );
    const totals = useMemo(() => costTotals(costed.map((c) => c.cost)), [costed]);

    /**
     * Per-line problems, keyed by row.
     *
     * AN EXPIRED PACK IS THE ONE THAT MATTERS. The server refuses it outright
     * (EXPIRED_ON_RECEIPT) and it is refused here too — not as a nicety, but
     * because a refusal that arrives after the clerk has keyed twenty lines and
     * pressed Post is a refusal they will work around by changing the date. Shown
     * on the row, while the carton is still in their hand and can go back on the
     * van, it is the correct answer instead.
     */
    const lineErrors = useMemo(() => {
        const map = {};
        costed.forEach(({ line, cost }) => {
            const problems = [];
            if (!line.medicineId) problems.push(t('grnpage.err_medicine', 'Pick a medicine'));
            if (!String(line.batchNo || '').trim()) {
                problems.push(t('grnpage.err_batch', 'Batch number — read it off the carton'));
            }
            if (!line.expiryDate) {
                if (!line.isProduct) {
                    problems.push(t('grnpage.err_expiry_missing', 'Expiry — a batch without one can never be sold'));
                }
            } else if (line.expiryDate < today) {
                problems.push(t('grnpage.err_expired', 'Already expired — do not accept it at the bay'));
            }
            if (!(cost.qtyPaid + cost.qtyFree > 0)) {
                problems.push(t('grnpage.err_qty', 'Quantity — paid or free must be more than zero'));
            }
            if (line.purchaseRate === '' || line.purchaseRate === null || line.purchaseRate === undefined) {
                // Not defaulted to zero anywhere. A rate that silently defaults
                // values the whole batch at nothing: the valuation drops, the
                // margin reads as total, and the expiry claim raised from it later
                // asks the supplier for zero rupees.
                problems.push(t('grnpage.err_rate', 'Rate — it is on the supplier invoice'));
            }
            if (!(num(line.mrp) > 0)) {
                problems.push(t('grnpage.err_mrp', 'MRP — it is printed on the pack'));
            }
            if (cost.overDiscounted) {
                problems.push(t('grnpage.err_discount', 'Discount is larger than the line'));
            }
            if (problems.length) map[line.key] = problems;
        });
        return map;
    }, [costed, today, t]);

    const hasLineErrors = Object.keys(lineErrors).length > 0;
    const expiredCount = costed.filter(
        ({ line }) => line.expiryDate && line.expiryDate < today
    ).length;

    // The reconcile check. Blank means the clerk has not typed the supplier's
    // total yet, which is not an error — it is simply a check not yet done.
    const printedP = printedTotal === '' ? null : scale2(printedTotal);
    const reconcileDiff = printedP === null ? null : unscale2(scale2(totals.grandTotal) - printedP);
    const reconciled = reconcileDiff !== null && Math.abs(reconcileDiff) < 0.005;

    /* ------------------------------------------------------------------ *
     * Entry: new, open, prefill from an order
     * ------------------------------------------------------------------ */

    const resetEntry = (keepHeader = false) => {
        setGrnId(null);
        setGrnNo('');
        setGrnStatus('draft');
        setPoId('');
        setPoNo('');
        setSupplierInvoiceNo('');
        setSupplierInvoiceDate('');
        setGrnDate(todayIst());
        setNotes('');
        setLines([]);
        setPrintedTotal('');
        setPickQuery('');
        setPickResults([]);
        if (!keepHeader) setSupplierId('');
    };

    const startNew = () => {
        resetEntry();
        setView('entry');
    };

    const openReceipt = async (row) => {
        setView('entry');
        setEntryLoading(true);
        try {
            const res = await axiosInstance.get(`/pharmacy/grn/${row.grnId}`);
            const grn = res?.data?.data;
            if (!grn) throw new Error('empty');
            setGrnId(grn.grnId);
            setGrnNo(grn.grnNo || '');
            setGrnStatus(grn.status || 'draft');
            setStoreId(grn.storeId || '');
            setSupplierId(grn.supplierId || '');
            setPoId(grn.poId || '');
            setPoNo(grn.poNo || '');
            setSupplierInvoiceNo(grn.supplierInvoiceNo || '');
            setSupplierInvoiceDate(grn.supplierInvoiceDate ? String(grn.supplierInvoiceDate).slice(0, 10) : '');
            setGrnDate(grn.grnDate ? String(grn.grnDate).slice(0, 10) : todayIst());
            setIsInterState(!!grn.isInterState);
            setNotes(grn.notes || '');
            setLines(
                (grn.items || []).map((item) => ({
                    ...blankLine(),
                    poItemId: item.poItemId || null,
                    medicineId: item.medicineId || '',
                    medicineName: item.medicineName || '',
                    isProduct: item.medicineType === 'product',
                    batchNo: item.batchNo || '',
                    expiryDate: item.expiryDate ? String(item.expiryDate).slice(0, 10) : '',
                    qtyReceived: item.qtyReceived === null || item.qtyReceived === undefined ? '' : String(item.qtyReceived),
                    qtyFree: num(item.qtyFree) ? String(item.qtyFree) : '',
                    purchaseRate: item.purchaseRate === null || item.purchaseRate === undefined ? '' : String(item.purchaseRate),
                    mrp: item.mrp === null || item.mrp === undefined ? '' : String(item.mrp),
                    discountPercent: num(item.discountPercent) ? String(item.discountPercent) : '',
                    discountAmount: num(item.discountAmount) ? String(item.discountAmount) : '',
                    gstRate: item.gstRate === null || item.gstRate === undefined ? '' : String(item.gstRate),
                }))
            );
            // A stored receipt's own grand total is what the clerk reconciled
            // against when it was keyed, so reopening it starts from a reconciled
            // state rather than an empty check.
            setPrintedTotal(grn.grandTotal === null || grn.grandTotal === undefined ? '' : String(grn.grandTotal));
        } catch (err) {
            toast.warn(apiError(err, t('grnpage.open_error', 'Could not open the goods receipt')));
            setView('register');
        } finally {
            setEntryLoading(false);
        }
    };

    /**
     * Pull the outstanding lines of a purchase order into the grid.
     *
     * The order already knows the medicine, the rate that was negotiated and the
     * slab; what it CANNOT know is the batch number and the expiry, because those
     * are facts about a carton that did not exist when the order was raised. So
     * the quantities and money are prefilled and the two columns only the person
     * holding the pack can supply are left deliberately empty.
     */
    const prefillFromOrder = async () => {
        const { value: enteredPoNo } = await MySwal.fire({
            title: t('grnpage.po_prompt_title', 'Receive against a purchase order'),
            input: 'text',
            inputLabel: t('grnpage.po_prompt_label', 'Order number, e.g. PO/25-26/000123'),
            inputPlaceholder: 'PO/25-26/000123',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            confirmButtonText: t('grnpage.po_prompt_ok', 'Find it'),
            cancelButtonText: t('grnpage.cancel', 'Cancel'),
        });
        if (!enteredPoNo) return;

        try {
            const listRes = await axiosInstance.get('/pharmacy/purchase-orders', {
                params: { q: enteredPoNo.trim(), limit: 5 },
            });
            const match = (listRes?.data?.data || []).find(
                (o) => String(o.poNo).toLowerCase() === enteredPoNo.trim().toLowerCase()
            ) || (listRes?.data?.data || [])[0];
            if (!match) {
                toast.warn(t('grnpage.po_not_found', 'No purchase order matches that number'));
                return;
            }

            const res = await axiosInstance.get(`/pharmacy/purchase-orders/${match.poId}`);
            const order = res?.data?.data;
            if (!order) return;

            if (order.status === 'draft' || order.status === 'cancelled') {
                // A draft was never sent, so nothing can have been delivered
                // against it; a cancelled one was withdrawn. The server refuses
                // both, and saying so here saves the clerk keying a receipt that
                // cannot be posted.
                toast.warn(
                    t('grnpage.po_not_receivable', 'Order {{no}} is {{status}} — it cannot be received against', {
                        no: order.poNo,
                        status: order.status,
                    })
                );
                return;
            }

            setStoreId(order.storeId || '');
            setSupplierId(order.supplierId || '');
            setPoId(order.poId);
            setPoNo(order.poNo || '');

            const outstanding = (order.items || [])
                .filter((i) => i.status !== 'cancelled' && i.status !== 'short_closed')
                .map((i) => ({
                    ...blankLine(),
                    poItemId: i.poItemId,
                    medicineId: i.medicineId,
                    medicineName: i.medicineName,
                    isProduct: i.medicineType === 'product',
                    packSize: i.packSize || '',
                    // What is still owed. A short supply is the normal case in
                    // Indian distribution — 100 ordered, 60 today, 40 when the
                    // stockist is replenished — so the balance is what prefills.
                    qtyReceived: String(Math.round((num(i.qtyOrdered) - num(i.qtyReceived)) * 100) / 100),
                    purchaseRate: i.rate === null || i.rate === undefined ? '' : String(i.rate),
                    gstRate: i.gstRate === null || i.gstRate === undefined ? '' : String(i.gstRate),
                }))
                .filter((i) => num(i.qtyReceived) > 0);

            if (!outstanding.length) {
                toast.info(t('grnpage.po_nothing_outstanding', 'Nothing is outstanding on {{no}}', { no: order.poNo }));
                return;
            }
            setLines((rows) => [...rows, ...outstanding]);
            toast.success(
                t('grnpage.po_prefilled', '{{n}} outstanding lines pulled in — key the batch and expiry off each carton', {
                    n: outstanding.length,
                })
            );
        } catch (err) {
            toast.warn(apiError(err, t('grnpage.po_error', 'Could not load the purchase order')));
        }
    };

    /* ------------------------------------------------------------------ *
     * Saving and posting
     * ------------------------------------------------------------------ */

    const payloadItems = () =>
        lines.map((line) => ({
            poItemId: line.poItemId || undefined,
            medicineId: line.medicineId,
            batchNo: String(line.batchNo || '').trim(),
            expiryDate: line.expiryDate,
            qtyReceived: num(line.qtyReceived),
            qtyFree: num(line.qtyFree),
            purchaseRate: num(line.purchaseRate),
            mrp: line.mrp === '' ? null : num(line.mrp),
            discountPercent: num(line.discountPercent),
            // Sent only when it was typed. Sending a derived amount would pin the
            // line to this screen's rounding instead of letting the server resolve
            // the percentage the supplier's bill actually prints.
            discountAmount:
                line.discountAmount === '' || line.discountAmount === null || line.discountAmount === undefined
                    ? undefined
                    : num(line.discountAmount),
            gstRate: num(line.gstRate),
        }));

    const headerProblem = () => {
        if (!storeId) return t('grnpage.err_store', 'Choose the counter this stock is being delivered to');
        if (!supplierId) return t('grnpage.err_supplier', 'Choose the supplier — a receipt with no party cannot be paid or returned');
        if (!lines.length) return t('grnpage.err_no_lines', 'Key at least one line from the supplier invoice');
        if (hasLineErrors) return t('grnpage.err_lines', 'Some lines are incomplete — see the red rows');
        return null;
    };

    /** Create the draft, or replace the lines of the one already open. */
    const saveDraft = async ({ silent = false } = {}) => {
        const problem = headerProblem();
        if (problem) {
            toast.warn(problem);
            return null;
        }

        const body = {
            storeId,
            supplierId,
            poId: poId || undefined,
            grnDate,
            supplierInvoiceNo: supplierInvoiceNo.trim() || undefined,
            supplierInvoiceDate: supplierInvoiceDate || undefined,
            isInterState,
            notes: notes.trim() || undefined,
            items: payloadItems(),
        };

        setSaving(true);
        try {
            let saved;
            if (grnId) {
                const res = await axiosInstance.put(`/pharmacy/grn/${grnId}`, body);
                saved = res?.data?.data;
            } else {
                const res = await axiosInstance.post('/pharmacy/grn', body);
                saved = res?.data?.data;
                setGrnId(saved?.grnId || null);
                setGrnNo(saved?.grnNo || '');
            }
            if (!silent) {
                toast.success(
                    t('grnpage.saved', 'Draft {{no}} saved — nothing is on the shelf until it is posted', {
                        no: saved?.grnNo || grnNo,
                    })
                );
            }
            return saved?.grnId || grnId;
        } catch (err) {
            if (apiCode(err) === 'GRN_ALREADY_POSTED') {
                // Not a save failure — the document moved underneath this screen.
                toast.warn(apiError(err, t('grnpage.already_posted', 'This receipt has already been posted')));
                setGrnStatus('posted');
            } else {
                toast.warn(apiError(err, t('grnpage.save_error', 'Could not save the draft')));
            }
            return null;
        } finally {
            setSaving(false);
        }
    };

    /**
     * Save, then post — the moment cartons become stock this hospital owns.
     *
     * Confirmed explicitly because it cannot be edited afterwards: a posted
     * receipt is corrected by cancelling it (which reverses every ledger row) and
     * receiving again, exactly as a wrong sale is corrected by a credit note
     * rather than by editing the bill. The confirmation says that, rather than
     * asking "are you sure".
     */
    const saveAndPost = async () => {
        const problem = headerProblem();
        if (problem) {
            toast.warn(problem);
            return;
        }

        const shelfQty = costed.reduce((sum, c) => sum + c.cost.qtyToStock, 0);
        const freeQty = costed.reduce((sum, c) => sum + c.cost.qtyFree, 0);

        const confirmed = await MySwal.fire({
            title: t('grnpage.post_title', 'Post this receipt?'),
            html: `
                <div style="text-align:left;font-size:13px;line-height:1.6">
                  <p>${t('grnpage.post_body', 'This puts <b>{{qty}}</b> units on the shelf at the chosen counter and writes the stock ledger. It cannot be edited afterwards — a mistake is corrected by cancelling and receiving again.', { qty: Math.round(shelfQty * 100) / 100 })}</p>
                  ${freeQty > 0 ? `<p style="margin-top:8px;color:#8a6127">${t('grnpage.post_free', '{{free}} of those are scheme (free) units — they go on the shelf and are not paid for.', { free: Math.round(freeQty * 100) / 100 })}</p>` : ''}
                  ${reconcileDiff !== null && !reconciled ? `<p style="margin-top:8px;color:#b91c1c"><b>${t('grnpage.post_mismatch', 'The total does not match the supplier bill you typed — off by Rs.{{diff}}.', { diff: money(Math.abs(reconcileDiff)) })}</b></p>` : ''}
                </div>`,
            icon: reconcileDiff !== null && !reconciled ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonColor: '#2D8A8A',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: t('grnpage.post_confirm', 'Post and create stock'),
            cancelButtonText: t('grnpage.cancel', 'Cancel'),
        });
        if (!confirmed.isConfirmed) return;

        const savedId = await saveDraft({ silent: true });
        if (!savedId) return;

        await postDraft(savedId, false);
    };

    const postDraft = async (id, allowDuplicateInvoice) => {
        setSaving(true);
        try {
            const res = await axiosInstance.post(`/pharmacy/grn/${id}/post`, { allowDuplicateInvoice });
            toast.success(
                t('grnpage.posted', '{{no}} posted — the stock is on the shelf', { no: res?.data?.data?.grnNo || grnNo })
            );
            setGrnStatus('posted');
            setView('register');
            resetEntry();
            refetch();
        } catch (err) {
            const code = apiCode(err);

            if (code === 'DUPLICATE_SUPPLIER_INVOICE') {
                // A WARNING A HUMAN OVERRIDES, NOT A WALL. Re-receiving a
                // cancelled receipt against the same supplier invoice is the
                // correct correction; paying the same bill twice is the most
                // expensive clerical error in a purchase department. So the
                // override exists and is never silent.
                const detail = err?.response?.data?.data || {};
                const again = await MySwal.fire({
                    title: t('grnpage.dupe_title', 'This bill has been received before'),
                    html: `<div style="text-align:left;font-size:13px;line-height:1.6">
                             <p>${apiError(err, '')}</p>
                             <p style="margin-top:8px">${t('grnpage.dupe_body', 'Receive it again only if this really is a second delivery on one invoice.')}</p>
                             ${detail.grnNo ? `<p style="margin-top:8px;color:#64748b">${t('grnpage.dupe_ref', 'Earlier receipt: {{no}}', { no: detail.grnNo })}</p>` : ''}
                           </div>`,
                    icon: 'warning',
                    showCancelButton: true,
                    confirmButtonColor: '#b45309',
                    cancelButtonColor: '#94a3b8',
                    confirmButtonText: t('grnpage.dupe_confirm', 'Receive it anyway'),
                    cancelButtonText: t('grnpage.cancel', 'Cancel'),
                });
                if (again.isConfirmed) await postDraft(id, true);
                return;
            }

            if (code === 'BATCH_RACE') {
                // Two deliveries of one lot posted at the same instant; the unique
                // index rejected the loser. Retrying finds the row the winner
                // created and tops it up, which is why this is offered as a retry
                // rather than shown as an error.
                toast.info(apiError(err, t('grnpage.race', 'Another delivery of one of these batches landed first — post again')));
                return;
            }

            toast.warn(apiError(err, t('grnpage.post_error', 'Could not post the goods receipt')));
        } finally {
            setSaving(false);
        }
    };

    /**
     * Cancel — the only way to undo a receipt.
     *
     * On a posted one this reverses every ledger row and takes the quantities back
     * off the shelf, so the server demands a reason and refuses outright if the
     * stock has since been issued (STOCK_ALREADY_ISSUED). Asking for the reason
     * here rather than sending a blank one keeps that refusal explicable.
     */
    const cancelReceipt = async (row) => {
        const posted = row.status === 'posted';
        const { value: reason, isConfirmed } = await MySwal.fire({
            title: t('grnpage.cancel_title', 'Cancel {{no}}?', { no: row.grnNo }),
            html: posted
                ? `<p style="font-size:13px;text-align:left">${t('grnpage.cancel_posted', 'This takes the received quantities back off the shelf and writes the reversing ledger entries. It will be refused if any of this delivery has already been issued.')}</p>`
                : `<p style="font-size:13px;text-align:left">${t('grnpage.cancel_draft', 'Nothing has moved, so this only closes the draft. Its number stays in the register rather than leaving a hole.')}</p>`,
            input: 'text',
            inputLabel: posted
                ? t('grnpage.cancel_reason_required', 'Reason (required — it explains the stock coming back off)')
                : t('grnpage.cancel_reason', 'Reason'),
            inputValidator: (value) =>
                posted && !String(value || '').trim()
                    ? t('grnpage.cancel_reason_missing', 'A reason is required to reverse posted stock')
                    : undefined,
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#94a3b8',
            confirmButtonText: t('grnpage.cancel_confirm', 'Cancel the receipt'),
            cancelButtonText: t('grnpage.keep', 'Keep it'),
        });
        if (!isConfirmed) return;

        try {
            await axiosInstance.post(`/pharmacy/grn/${row.grnId}/cancel`, { reason: String(reason || '').trim() || undefined });
            toast.success(t('grnpage.cancelled', '{{no}} cancelled', { no: row.grnNo }));
            refetch();
        } catch (err) {
            if (apiCode(err) === 'STOCK_ALREADY_ISSUED') {
                // The conditional decrement refused, which means the hospital has
                // sold goods it would now be claiming it never received. That is a
                // decision for a human, so the message is shown in full rather
                // than reduced to a toast line.
                MySwal.fire({
                    title: t('grnpage.issued_title', 'Some of this delivery has already been issued'),
                    text: apiError(err, ''),
                    icon: 'error',
                    confirmButtonColor: '#4ABDBD',
                });
                return;
            }
            toast.warn(apiError(err, t('grnpage.cancel_error', 'Could not cancel the receipt')));
        }
    };

    /* ------------------------------------------------------------------ *
     * Render
     * ------------------------------------------------------------------ */

    const indexOfFirst = (currentPage - 1) * itemsPerPage;
    const supplierName = suppliers.find((s) => s.supplierId === supplierId)?.name || '';
    const readOnly = grnStatus === 'posted' || grnStatus === 'cancelled';

    const StatusBadge = ({ status }) => (
        <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-bold border capitalize ${STATUS_TONE[status] || STATUS_TONE.draft}`}>
            {t(`grnpage.status_${status}`, status)}
        </span>
    );

    const cellClass =
        'w-full px-2 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] disabled:bg-slate-50 disabled:text-slate-400';

    return (
        <div data-testid="grn-page-container" className="bg-white p-4 rounded-2xl shadow-md sm:p-6 w-full">
            <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
                <div className="ml-2">
                    <h1 data-testid="grn-page-title" className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                        {t('grnpage.title', 'Goods receipt')}
                    </h1>
                    <p className="text-xs text-slate-400 mt-1">
                        {t('grnpage.subtitle', 'The only document that puts stock on a shelf. Key the distributor invoice, check the total against the paper, then post.')}
                    </p>
                </div>

                {view === 'register' ? (
                    <button
                        data-testid="grn-new-button"
                        onClick={startNew}
                        disabled={!canPurchase}
                        title={canPurchase ? undefined : t('grnpage.no_permission', 'You do not have permission to receive stock')}
                        className={`group flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg transition-all duration-300 font-semibold text-sm whitespace-nowrap ${canPurchase ? 'cursor-pointer hover:shadow-xl hover:scale-105' : 'opacity-50 cursor-not-allowed'}`}
                    >
                        <PackagePlus className="w-5 h-5" />
                        {t('grnpage.new', 'Receive a delivery')}
                    </button>
                ) : (
                    <button
                        data-testid="grn-back-button"
                        onClick={() => { setView('register'); resetEntry(); refetch(); }}
                        className="flex items-center gap-2 w-full sm:w-auto px-4 py-2 bg-slate-100 text-slate-600 rounded-xl shadow transition-all duration-300 font-semibold text-sm hover:bg-slate-200 cursor-pointer"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        {t('grnpage.back', 'Back to the register')}
                    </button>
                )}
            </div>

            {/* ============================ REGISTER ============================ */}
            {view === 'register' && (
                <>
                    <div className="mb-4 flex flex-col sm:flex-row gap-2 sm:items-center flex-wrap">
                        <div className="relative w-full sm:w-72">
                            <input
                                data-testid="grn-search-input"
                                type="text"
                                placeholder={t('grnpage.search_placeholder', 'Search receipt number…')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value.replace(/[<>]/g, '').slice(0, 40))}
                                maxLength={40}
                                className="w-full p-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                            />
                            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                        </div>

                        <select
                            data-testid="grn-status-filter"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                            className="tt-select w-full sm:w-40 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        >
                            <option value="">{t('grnpage.all_statuses', 'All statuses')}</option>
                            <option value="draft">{t('grnpage.status_draft', 'Draft')}</option>
                            <option value="posted">{t('grnpage.status_posted', 'Posted')}</option>
                            <option value="cancelled">{t('grnpage.status_cancelled', 'Cancelled')}</option>
                        </select>

                        <select
                            data-testid="grn-store-filter"
                            value={storeFilter}
                            onChange={(e) => { setStoreFilter(e.target.value); setCurrentPage(1); }}
                            className="tt-select w-full sm:w-52 p-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                        >
                            <option value="">{t('grnpage.all_stores', 'All counters')}</option>
                            {stores.map((s) => (
                                <option key={s.storeId} value={s.storeId}>{s.name}</option>
                            ))}
                        </select>
                    </div>

                    {listLoading ? (
                        <div className="flex flex-col items-center justify-center py-4 pt-12">
                            <CommonLoader />
                            <span className="text-xl text-[#4ABDBD]">{t('grnpage.loading', 'Loading...')}</span>
                        </div>
                    ) : (
                        <>
                            <div className="hidden lg:block overflow-x-auto rounded-md border border-gray-200">
                                <table data-testid="grn-table" className="w-full border-collapse rounded-md overflow-hidden">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.serial_no', 'S.No')}</th>
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.grn_no', 'Receipt')}</th>
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.supplier', 'Supplier')}</th>
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.supplier_invoice', 'Their invoice')}</th>
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.store', 'Counter')}</th>
                                            <th className="p-3 text-right text-xs font-bold uppercase tracking-wider">{t('grnpage.value', 'Value')}</th>
                                            <th className="p-3 text-left text-xs font-bold uppercase tracking-wider">{t('grnpage.status', 'Status')}</th>
                                            <th className="p-3 text-center text-xs font-bold uppercase tracking-wider">{t('grnpage.action', 'Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody data-testid="grn-table-body">
                                        {receipts.length > 0 ? receipts.map((row, index) => (
                                            <tr
                                                key={row.grnId}
                                                data-testid={`grn-row-${row.grnId}`}
                                                className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300"
                                            >
                                                <td className="p-3 text-xs text-gray-600">{indexOfFirst + index + 1}</td>
                                                <td className="p-3 text-xs">
                                                    <div className="font-semibold text-gray-700 font-mono">{row.grnNo}</div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {row.grnDate ? String(row.grnDate).slice(0, 10) : '—'}
                                                        {row.poNo ? ` · ${row.poNo}` : ''}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">{row.supplierName || '—'}</td>
                                                <td className="p-3 text-xs text-gray-600">
                                                    <div className="font-mono">{row.supplierInvoiceNo || '—'}</div>
                                                    <div className="text-[11px] text-slate-400">
                                                        {row.supplierInvoiceDate ? String(row.supplierInvoiceDate).slice(0, 10) : ''}
                                                    </div>
                                                </td>
                                                <td className="p-3 text-xs text-gray-600">{row.storeName || '—'}</td>
                                                <td className="p-3 text-xs text-right font-semibold text-gray-700">
                                                    {money(row.grandTotal)}
                                                    {row.isInterState && (
                                                        <div className="text-[10px] text-slate-400 font-normal">{t('grnpage.igst', 'IGST')}</div>
                                                    )}
                                                </td>
                                                <td className="p-3 text-xs"><StatusBadge status={row.status} /></td>
                                                <td className="p-3 text-xs">
                                                    <div className="flex justify-center gap-2">
                                                        <button
                                                            data-testid={`grn-open-${row.grnId}`}
                                                            onClick={() => openReceipt(row)}
                                                            title={t('grnpage.open', 'Open')}
                                                            className="text-[#4ABDBD] p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 shadow-md transition-all duration-300 hover:text-white hover:from-[#4ABDBD] hover:to-[#3AA8A8] cursor-pointer hover:scale-110"
                                                        >
                                                            <FileText className="h-4 w-4" />
                                                        </button>
                                                        {row.status !== 'cancelled' && (
                                                            <button
                                                                data-testid={`grn-cancel-${row.grnId}`}
                                                                onClick={() => cancelReceipt(row)}
                                                                disabled={!canPurchase}
                                                                title={t('grnpage.cancel_action', 'Cancel')}
                                                                className={`text-red-500 p-2 rounded-xl bg-gradient-to-r from-red-50 to-pink-50 shadow-md transition-all duration-300 ${canPurchase ? 'hover:text-white hover:from-red-500 hover:to-pink-500 cursor-pointer hover:scale-110' : 'opacity-40 cursor-not-allowed'}`}
                                                            >
                                                                <Ban className="h-4 w-4" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan={8} className="p-6 text-center text-gray-500">
                                                    {t('grnpage.no_receipts', 'Nothing received yet. Every strip on a shelf starts as a line on one of these.')}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Cards below lg — the pharmacy office runs on tablets too. */}
                            <div className="block lg:hidden space-y-4">
                                {receipts.length > 0 ? receipts.map((row) => (
                                    <div key={row.grnId} data-testid={`grn-card-${row.grnId}`} className="p-4 bg-gray-50 rounded-xl shadow-md">
                                        <div className="flex justify-between items-start gap-2">
                                            <div>
                                                <div className="font-mono font-semibold text-gray-800 text-sm">{row.grnNo}</div>
                                                <div className="text-xs text-gray-600 mt-1">{row.supplierName || '—'}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {row.supplierInvoiceNo ? `${t('grnpage.their_bill', 'Their bill')} ${row.supplierInvoiceNo}` : ''}
                                                </div>
                                            </div>
                                            <StatusBadge status={row.status} />
                                        </div>
                                        <div className="flex justify-between items-center mt-3">
                                            <span className="text-sm font-bold text-gray-700">{money(row.grandTotal)}</span>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => openReceipt(row)}
                                                    className="text-[#4ABDBD] p-2 rounded-xl bg-white shadow cursor-pointer"
                                                >
                                                    <FileText className="h-4 w-4" />
                                                </button>
                                                {row.status !== 'cancelled' && canPurchase && (
                                                    <button
                                                        onClick={() => cancelReceipt(row)}
                                                        className="text-red-500 p-2 rounded-xl bg-white shadow cursor-pointer"
                                                    >
                                                        <Ban className="h-4 w-4" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="p-6 text-center text-gray-500 text-sm">
                                        {t('grnpage.no_receipts', 'Nothing received yet. Every strip on a shelf starts as a line on one of these.')}
                                    </div>
                                )}
                            </div>

                            <Pagination
                                totalItems={total}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                onPageChange={(page, perPage) => {
                                    setCurrentPage(page);
                                    if (perPage) setItemsPerPage(perPage);
                                }}
                            />
                        </>
                    )}
                </>
            )}

            {/* ============================== ENTRY ============================== */}
            {view === 'entry' && (
                entryLoading ? (
                    <div className="flex flex-col items-center justify-center py-4 pt-12">
                        <CommonLoader />
                        <span className="text-xl text-[#4ABDBD]">{t('grnpage.loading', 'Loading...')}</span>
                    </div>
                ) : (
                    <>
                        {readOnly && (
                            <div data-testid="grn-readonly-banner" className="mb-4 px-3 py-2 rounded-xl bg-slate-100 border border-slate-300 text-xs text-slate-700 flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>
                                    {grnStatus === 'posted'
                                        ? t('grnpage.readonly_posted', '{{no}} is posted, so it can no longer be edited — its stock is on the shelf and its ledger entries are permanent. Correct it by cancelling and receiving again.', { no: grnNo })
                                        : t('grnpage.readonly_cancelled', '{{no}} was cancelled. It is kept so its number is accounted for in the register.', { no: grnNo })}
                                </span>
                            </div>
                        )}

                        {/* ---- the invoice's identity ---- */}
                        <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-4 p-4 rounded-2xl bg-gradient-to-r from-slate-50 to-blue-50/40 border border-slate-200">
                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.store', 'Counter')}
                                </label>
                                <select
                                    data-testid="grn-store"
                                    value={storeId}
                                    disabled={readOnly || !!grnId}
                                    onChange={(e) => setStoreId(e.target.value)}
                                    className={`tt-select ${cellClass}`}
                                >
                                    <option value="">{t('grnpage.choose', 'Choose…')}</option>
                                    {stores.map((s) => (
                                        <option key={s.storeId} value={s.storeId}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.supplier', 'Supplier')}
                                </label>
                                <select
                                    data-testid="grn-supplier"
                                    value={supplierId}
                                    disabled={readOnly || !!grnId}
                                    onChange={(e) => {
                                        const next = e.target.value;
                                        setSupplierId(next);
                                        // The place of supply follows the supplier's GSTIN against
                                        // the counter's: the first two digits of a GSTIN are the
                                        // state code. Getting it wrong does not fail — it books
                                        // CGST+SGST where the invoice says IGST, and the input tax
                                        // credit is then rejected at reconciliation, months later,
                                        // with the money already paid. The server derives it too;
                                        // this only spares the clerk a tick they would forget.
                                        const supplier = suppliers.find((s) => s.supplierId === next);
                                        const store = stores.find((s) => s.storeId === storeId);
                                        const code = (v) => (/^\d{2}/.test(String(v || '')) ? String(v).slice(0, 2) : null);
                                        const a = code(store?.gstin);
                                        const b = code(supplier?.gstin);
                                        if (a && b) setIsInterState(a !== b);
                                    }}
                                    className={`tt-select ${cellClass}`}
                                >
                                    <option value="">{t('grnpage.choose', 'Choose…')}</option>
                                    {suppliers.map((s) => (
                                        <option key={s.supplierId} value={s.supplierId}>{s.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.supplier_invoice_no', 'Their invoice no.')}
                                </label>
                                <input
                                    data-testid="grn-supplier-invoice"
                                    type="text"
                                    value={supplierInvoiceNo}
                                    disabled={readOnly}
                                    onChange={(e) => setSupplierInvoiceNo(e.target.value.replace(/[<>]/g, '').slice(0, 40))}
                                    placeholder={t('grnpage.supplier_invoice_hint', 'as printed')}
                                    className={cellClass}
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.supplier_invoice_date', 'Their invoice date')}
                                </label>
                                <input
                                    data-testid="grn-supplier-invoice-date"
                                    type="date"
                                    value={supplierInvoiceDate}
                                    disabled={readOnly}
                                    onChange={(e) => setSupplierInvoiceDate(e.target.value)}
                                    className={cellClass}
                                />
                            </div>

                            <div>
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.grn_date', 'Received on')}
                                </label>
                                <input
                                    data-testid="grn-date"
                                    type="date"
                                    value={grnDate}
                                    disabled={readOnly}
                                    onChange={(e) => setGrnDate(e.target.value)}
                                    className={cellClass}
                                />
                            </div>

                            <div className="flex flex-col justify-end gap-2">
                                <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                                    <input
                                        data-testid="grn-interstate"
                                        type="checkbox"
                                        checked={isInterState}
                                        disabled={readOnly}
                                        onChange={(e) => setIsInterState(e.target.checked)}
                                        className="w-4 h-4 accent-[#4ABDBD]"
                                    />
                                    {t('grnpage.interstate', 'Inter-state (IGST)')}
                                </label>
                                {!readOnly && (
                                    <button
                                        data-testid="grn-prefill-po"
                                        onClick={prefillFromOrder}
                                        className="flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-white border border-[#4ABDBD]/40 text-[#2D8A8A] text-[11px] font-semibold hover:bg-[#4ABDBD]/10 cursor-pointer"
                                    >
                                        <ClipboardList className="w-3.5 h-3.5" />
                                        {poNo || t('grnpage.from_po', 'Pull from an order')}
                                    </button>
                                )}
                            </div>
                        </div>

                        {grnNo && (
                            <div className="mb-3 flex items-center gap-2 text-sm">
                                <Truck className="w-4 h-4 text-[#2D8A8A]" />
                                <span className="font-mono font-bold text-gray-700">{grnNo}</span>
                                <StatusBadge status={grnStatus} />
                                {supplierName && <span className="text-xs text-slate-400">· {supplierName}</span>}
                            </div>
                        )}

                        {/* ---- the medicine picker ---- */}
                        {!readOnly && (
                            <div ref={pickBoxRef} className="relative mb-3 w-full sm:w-[28rem]">
                                <input
                                    data-testid="grn-medicine-search"
                                    type="text"
                                    value={pickQuery}
                                    onChange={(e) => setPickQuery(e.target.value.replace(/[<>]/g, '').slice(0, 60))}
                                    onFocus={() => pickResults.length && setPickOpen(true)}
                                    disabled={!storeId}
                                    placeholder={
                                        storeId
                                            ? t('grnpage.pick_placeholder', 'Add a line — search name / salt / brand / barcode…')
                                            : t('grnpage.pick_needs_store', 'Choose the counter first')
                                    }
                                    className="w-full p-2 pl-10 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] disabled:bg-slate-50"
                                />
                                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4ABDBD]" />
                                {picking && <Loader2 className="h-4 w-4 absolute right-3 top-1/2 -translate-y-1/2 text-[#4ABDBD] animate-spin" />}

                                {pickOpen && pickResults.length > 0 && (
                                    <div
                                        data-testid="grn-medicine-results"
                                        className="absolute z-30 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-xl"
                                    >
                                        {pickResults.map((m) => (
                                            <button
                                                key={m.medicineId}
                                                onClick={() => addMedicineLine(m)}
                                                className="w-full text-left px-3 py-2 hover:bg-[#4ABDBD]/10 border-b border-gray-100 last:border-0 cursor-pointer"
                                            >
                                                <div className="text-xs font-semibold text-gray-700">{m.name}</div>
                                                <div className="text-[11px] text-slate-400">
                                                    {[m.brand, m.strength, m.packSize].filter(Boolean).join(' · ')}
                                                    {m.gstRate !== undefined ? ` · ${t('grnpage.gst_at', 'GST {{rate}}%', { rate: num(m.gstRate) })}` : ''}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {expiredCount > 0 && (
                            <div data-testid="grn-expired-warning" className="mb-3 px-3 py-2 rounded-xl bg-red-50 border border-red-300 text-xs text-red-700 flex items-start gap-2">
                                <CalendarClock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                <span>
                                    {t('grnpage.expired_warning', '{{n}} line(s) are already out of date. Expired stock cannot be received — send those cartons back on the van rather than putting them where the counter can pick them.', { n: expiredCount })}
                                </span>
                            </div>
                        )}

                        {/* ---- THE GRID ---- */}
                        <div className="overflow-x-auto rounded-md border border-gray-200">
                            <table data-testid="grn-lines-table" className="w-full border-collapse min-w-[1180px]">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white">
                                        <th className="p-2 text-left text-[11px] font-bold uppercase tracking-wider w-56">{t('grnpage.medicine', 'Medicine')}</th>
                                        <th className="p-2 text-left text-[11px] font-bold uppercase tracking-wider w-28">{t('grnpage.batch', 'Batch')}</th>
                                        <th className="p-2 text-left text-[11px] font-bold uppercase tracking-wider w-36">{t('grnpage.expiry', 'Expiry')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-20">{t('grnpage.qty', 'Qty')}</th>
                                        {/* Its own column, never folded into the paid quantity. See the file header. */}
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-20 bg-[#D7A05C]/30">{t('grnpage.free', 'Free')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-24">{t('grnpage.rate', 'Rate')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-24">{t('grnpage.mrp', 'MRP')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-20">{t('grnpage.disc_pc', 'Disc %')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-24">{t('grnpage.disc_amt', 'Disc ₹')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-20">{t('grnpage.gst', 'GST %')}</th>
                                        <th className="p-2 text-right text-[11px] font-bold uppercase tracking-wider w-28">{t('grnpage.line_total', 'Line total')}</th>
                                        <th className="p-2 text-center text-[11px] font-bold uppercase tracking-wider w-12" />
                                    </tr>
                                </thead>
                                <tbody data-testid="grn-lines-body">
                                    {costed.length > 0 ? costed.map(({ line, cost }) => {
                                        const problems = lineErrors[line.key];
                                        const expired = line.expiryDate && line.expiryDate < today;
                                        return (
                                            <tr
                                                key={line.key}
                                                data-testid={`grn-line-${line.key}`}
                                                className={`border-b border-gray-100 align-top ${expired ? 'bg-red-50/70' : problems ? 'bg-amber-50/40' : ''}`}
                                            >
                                                <td className="p-2">
                                                    <div className="text-xs font-semibold text-gray-700">{line.medicineName || '—'}</div>
                                                    <div className="text-[10px] text-slate-400">
                                                        {[line.packSize, line.poItemId ? t('grnpage.against_po', 'against order') : null].filter(Boolean).join(' · ')}
                                                    </div>
                                                    {problems && (
                                                        <ul className="mt-1 space-y-0.5">
                                                            {problems.map((p) => (
                                                                <li key={p} className="text-[10px] text-red-600 font-semibold flex items-start gap-1">
                                                                    <AlertTriangle className="w-3 h-3 mt-px flex-shrink-0" />
                                                                    {p}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-batchno-${line.key}`}
                                                        type="text"
                                                        value={line.batchNo}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { batchNo: e.target.value.replace(/[<>]/g, '').slice(0, 30) })}
                                                        className={cellClass}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    {/* `min` makes a past date unreachable in the picker;
                                                        the validator above still refuses one typed by hand,
                                                        because a browser is free to ignore min and the
                                                        consequence — an expired pack on a sellable shelf —
                                                        is not one to leave to the widget. */}
                                                    <input
                                                        data-testid={`grn-expiry-${line.key}`}
                                                        type="date"
                                                        value={line.expiryDate}
                                                        min={today}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { expiryDate: e.target.value })}
                                                        className={`${cellClass} ${expired ? 'border-red-400 text-red-600' : ''}`}
                                                    />
                                                    {line.isProduct && !line.expiryDate && (
                                                        <div data-testid={`grn-expiry-optional-${line.key}`} className="text-[10px] text-slate-400 mt-0.5">
                                                            {t('grnpage.expiry_optional_product', 'Optional for a product')}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-qty-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={line.qtyReceived}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { qtyReceived: e.target.value })}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2 bg-[#D7A05C]/10">
                                                    <input
                                                        data-testid={`grn-free-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={line.qtyFree}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { qtyFree: e.target.value })}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                    {cost.qtyFree > 0 && (
                                                        <div className="mt-1 text-[10px] text-[#8a6127] font-semibold flex items-center gap-1">
                                                            <Gift className="w-3 h-3" />
                                                            {t('grnpage.to_shelf', '{{n}} to shelf', { n: cost.qtyToStock })}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-rate-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={line.purchaseRate}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { purchaseRate: e.target.value })}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-mrp-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={line.mrp}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { mrp: e.target.value })}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-discpc-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        value={line.discountPercent}
                                                        disabled={readOnly || line.discountAmount !== ''}
                                                        onChange={(e) => patchLine(line.key, { discountPercent: e.target.value })}
                                                        title={line.discountAmount !== '' ? t('grnpage.disc_amt_wins', 'A typed rupee discount wins over the percentage') : undefined}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-discamt-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={line.discountAmount}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { discountAmount: e.target.value })}
                                                        placeholder={cost.discountAmount ? money(cost.discountAmount) : ''}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2">
                                                    <input
                                                        data-testid={`grn-gst-${line.key}`}
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        step="0.01"
                                                        value={line.gstRate}
                                                        disabled={readOnly}
                                                        onChange={(e) => patchLine(line.key, { gstRate: e.target.value })}
                                                        className={`${cellClass} text-right`}
                                                    />
                                                </td>
                                                <td className="p-2 text-right">
                                                    <div className="text-xs font-bold text-gray-700">{money(cost.lineTotal)}</div>
                                                    {/* Taxable and tax spelled out beneath, because this is
                                                        the pair the clerk checks against the supplier's own
                                                        line — and because a purchase adds tax on top rather
                                                        than backing it out, which is not what anybody who
                                                        works the sales counter expects. */}
                                                    <div className="text-[10px] text-slate-400">
                                                        {t('grnpage.taxable_short', 'tax base')} {money(cost.taxableAmount)}
                                                    </div>
                                                    <div className="text-[10px] text-slate-400">
                                                        {isInterState
                                                            ? `${t('grnpage.igst', 'IGST')} ${money(cost.igst)}`
                                                            : `${t('grnpage.cgst_sgst', 'C+S')} ${money(cost.cgst)}+${money(cost.sgst)}`}
                                                    </div>
                                                </td>
                                                <td className="p-2 text-center">
                                                    {!readOnly && (
                                                        <button
                                                            data-testid={`grn-remove-${line.key}`}
                                                            onClick={() => removeLine(line.key)}
                                                            title={t('grnpage.remove_line', 'Remove line')}
                                                            className="text-red-500 p-1.5 rounded-lg bg-red-50 hover:bg-red-500 hover:text-white transition-colors cursor-pointer"
                                                        >
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    }) : (
                                        <tr>
                                            <td colSpan={12} className="p-8 text-center text-gray-500 text-sm">
                                                {t('grnpage.no_lines', 'No lines yet. Search for a medicine above, or pull the outstanding lines from a purchase order.')}
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ---- TOTALS AND THE RECONCILE CHECK ---- */}
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                            <div className="lg:col-span-2 p-4 rounded-2xl bg-slate-50 border border-slate-200">
                                <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                    {t('grnpage.notes', 'Notes')}
                                </label>
                                <textarea
                                    data-testid="grn-notes"
                                    value={notes}
                                    disabled={readOnly}
                                    rows={3}
                                    onChange={(e) => setNotes(e.target.value.slice(0, 500))}
                                    placeholder={t('grnpage.notes_hint', 'Short delivery, damaged carton, anything the next person needs to know')}
                                    className="w-full p-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] disabled:bg-slate-100"
                                />

                                <div className="mt-3">
                                    <label className="block text-[11px] font-semibold text-slate-500 mb-1">
                                        {t('grnpage.printed_total', "Total printed on the supplier's bill")}
                                    </label>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <input
                                            data-testid="grn-printed-total"
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={printedTotal}
                                            onChange={(e) => setPrintedTotal(e.target.value)}
                                            placeholder="0.00"
                                            className="w-40 p-2 border border-gray-300 rounded-lg text-sm text-right focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]"
                                        />
                                        {reconcileDiff === null ? (
                                            <span className="text-[11px] text-slate-400">
                                                {t('grnpage.reconcile_hint', 'Type it in — if it matches the total below, the invoice is keyed correctly.')}
                                            </span>
                                        ) : reconciled ? (
                                            <span data-testid="grn-reconciled" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-300 text-[11px] font-bold">
                                                {t('grnpage.reconciled', 'Matches the paper')}
                                            </span>
                                        ) : (
                                            <span data-testid="grn-mismatch" className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-red-50 text-red-700 border border-red-300 text-[11px] font-bold">
                                                <AlertTriangle className="w-3.5 h-3.5" />
                                                {t('grnpage.mismatch', 'Off by {{diff}} — a line is mis-keyed', { diff: money(Math.abs(reconcileDiff)) })}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div data-testid="grn-totals" className="p-4 rounded-2xl bg-gradient-to-br from-[#4ABDBD]/10 to-white border border-[#4ABDBD]/30">
                                <div className="space-y-1.5 text-xs">
                                    <div className="flex justify-between text-slate-600">
                                        <span>{t('grnpage.gross', 'Goods value')}</span><span className="font-mono">{money(totals.subTotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-600">
                                        <span>{t('grnpage.discount', 'Discount')}</span><span className="font-mono">-{money(totals.discountTotal)}</span>
                                    </div>
                                    <div className="flex justify-between text-slate-700 font-semibold border-t border-slate-200 pt-1.5">
                                        <span>{t('grnpage.taxable', 'Taxable')}</span><span className="font-mono">{money(totals.taxableTotal)}</span>
                                    </div>
                                    {isInterState ? (
                                        <div className="flex justify-between text-slate-600">
                                            <span>{t('grnpage.igst', 'IGST')}</span><span className="font-mono">{money(totals.igstTotal)}</span>
                                        </div>
                                    ) : (
                                        <>
                                            <div className="flex justify-between text-slate-600">
                                                <span>{t('grnpage.cgst', 'CGST')}</span><span className="font-mono">{money(totals.cgstTotal)}</span>
                                            </div>
                                            <div className="flex justify-between text-slate-600">
                                                <span>{t('grnpage.sgst', 'SGST')}</span><span className="font-mono">{money(totals.sgstTotal)}</span>
                                            </div>
                                        </>
                                    )}
                                    <div className="flex justify-between text-slate-500">
                                        <span>{t('grnpage.round_off', 'Round off')}</span><span className="font-mono">{money(totals.roundOff)}</span>
                                    </div>
                                    <div className="flex justify-between text-base font-bold text-[#2D8A8A] border-t border-[#4ABDBD]/40 pt-2 mt-1">
                                        <span>{t('grnpage.grand_total', 'Payable')}</span>
                                        <span data-testid="grn-grand-total" className="font-mono">{money(totals.grandTotal)}</span>
                                    </div>
                                </div>

                                {!readOnly && (
                                    <div className="mt-4 space-y-2">
                                        <button
                                            data-testid="grn-save-draft"
                                            onClick={() => saveDraft()}
                                            disabled={saving || !canPurchase}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-white border border-[#4ABDBD]/50 text-[#2D8A8A] font-semibold text-sm transition-all ${saving || !canPurchase ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#4ABDBD]/10 cursor-pointer'}`}
                                        >
                                            <Save className="w-4 h-4" />
                                            {t('grnpage.save_draft', 'Save draft')}
                                        </button>
                                        <button
                                            data-testid="grn-post"
                                            onClick={saveAndPost}
                                            disabled={saving || !canPurchase || hasLineErrors || !lines.length}
                                            title={hasLineErrors ? t('grnpage.fix_lines', 'Fix the lines marked in red first') : undefined}
                                            className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white font-semibold text-sm shadow-lg transition-all ${saving || !canPurchase || hasLineErrors || !lines.length ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-xl hover:scale-[1.02] cursor-pointer'}`}
                                        >
                                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                                            {t('grnpage.post_action', 'Post — put it on the shelf')}
                                        </button>
                                        <p className="text-[10px] text-slate-400 leading-snug">
                                            {t('grnpage.post_note', 'Posting creates the batches and writes the stock ledger. A posted receipt cannot be edited — only cancelled and re-keyed.')}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* There is deliberately no "add blank line" button. A line
                            with no medicine on it can never be completed — the only
                            way to name a product is the search above, because a
                            free-typed drug name is how a receipt ends up creating
                            stock against a medicine the catalogue does not have. */}
                    </>
                )
            )}
        </div>
    );
}
